import { ComponentType } from '@angular/cdk/overlay/index';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { BreakPoints } from '@app/common/breakpoints';
import { Environment } from '@environments/interface.environment';
import { Constants } from '@app/common/constants';
import { FormControl, ValidatorFn } from '@angular/forms';
import { ComponentState } from '@app/models/component-state';
import { ElementRef, ComponentFactoryResolver, ViewContainerRef } from '@angular/core';
import { Route } from '@angular/router';
import { NewsPage } from '@app/models/news-page';
import { HttpErrorResponse } from '@angular/common/http';

export class Utilities {

  public static nameof = <T>(name: keyof T) => name;

  public static openSnackBar<T>(snackbar: MatSnackBar, component: ComponentType<T>, id: string, duration: number = 2500, error?: HttpErrorResponse): MatSnackBarRef<T> {
    const snackBar = snackbar.openFromComponent<T>(component, {
      verticalPosition: 'bottom',
      data: {id, error},
      horizontalPosition: 'right',
      panelClass: ['workplace-snackbar'],
      duration: duration
    });
    return snackBar;
  }

  /**
   * https://github.com/ReactiveX/rxjs/blob/master/src/internal/util/isNumeric.ts
   * @param val
   */
  public static isNumeric(val: any): val is number | string {
    // parseFloat NaNs numeric-cast false positives (null|true|false|"")
    // ...but misinterprets leading-number strings, particularly hex literals ("0x...")
    // subtraction forces infinities to NaN
    // adding 1 corrects loss of precision from parseFloat (#15100)
    return !Array.isArray(val) && (val - parseFloat(val) + 1) >= 0;
  }

  public static isUrl(value: string) {
    const decodedValue = decodeURI(value);
    const encodedValue = encodeURI(decodedValue);
    return /^(?:http(s)?:\/\/)([\w.-])+(?:[\w\.-]+)+([\w\-\.%_~:/?#[\]@!\$&'\(\)\*\+,;=.])+$/.test(encodedValue);
  }

  public static getRealUrlFromTokenBasedUrl(environment: Environment, url: string) {
    if (url) {
      if (url.indexOf('<tenant>') !== -1) {
        const matches = environment.coreWorkplaceSiteUrl.match(/https?:\/\/([a-z0-9]+)/);
        if (matches.length > 0) {
          return url.replace('<tenant>', matches[1]);
        } else {
          return url;
        }
      } else if (url.indexOf('<coreRootSite>') !== -1) {
        return url.replace('<coreRootSite>', environment.coreWorkplaceSiteUrl);
      } else if (url.indexOf('<newsCenterSite>') !== -1) {
        return url.replace('<newsCenterSite>', environment.featureSettings.newsCenterSiteUrl ? environment.featureSettings.newsCenterSiteUrl : environment.coreWorkplaceSiteUrl);
      } else {
        return url;
      }
    } else {
      return null;
    }
  }

  /**
 * detect IE
 * returns version of IE or -1, if browser is not Internet Explorer
 */
  public static detectIE(userAgent: string): number {

    const msie = userAgent.indexOf('MSIE ');
    if (msie > 0) {
      // IE 10 or older => return version number
      return parseInt(userAgent.substring(msie + 5, userAgent.indexOf('.', msie)), 10);
    }

    const trident = userAgent.indexOf('Trident/');
    if (trident > 0) {
      // IE 11 => return version number
      const rv = userAgent.indexOf('rv:');
      return parseInt(userAgent.substring(rv + 3, userAgent.indexOf('.', rv)), 10);
    }

    const edge = userAgent.indexOf('Edge/');
    if (edge > 0) {
      // Edge (IE 12+) => return version number
      return parseInt(userAgent.substring(edge + 5, userAgent.indexOf('.', edge)), 10);
    }

    // other browser
    return -1;
  }

  public static getRootUrlFromInput(input) {
    if (input) {
      input = input.replace(/(www\.)/i, '');
      if (!input.replace(/(www\.)/i, '')) {
        input = 'https://' + input;
      }

      const reg = /:\/\/(.[^/]+)/;
      return 'https://' + input.match(reg)[1];
    }
    return '';
  }

  public static generateInitials(title: string): string {
    if (title) {
      const upperCaseLetters = title.replace(/[^A-Z]/g, '');
      if (upperCaseLetters.length >= 2) {
        return upperCaseLetters.substr(0, 2).toUpperCase();
      } else {
        return title.substr(0, 2).toUpperCase();
      }
    }
    return null;
  }

  public static truncate(text: string, maxLength: number): string {
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength - 1) + '\u2026';
    } else {
      return text;
    }
  }

  /*
 * Matches the file type to a specfic icon.
 */
  public static getMSIcon(fileType: string): string {
    const type = fileType ? fileType.toLowerCase() : '';
    let iconClasses = 'ms-Icon ';

    switch (type) {
      case 'pdf':
        iconClasses += 'ms-Icon--PDF';
        break;
      case 'word':
        iconClasses += 'ms-Icon--WordLogo';
        break;
      case 'docx':
        iconClasses += 'ms-Icon--WordLogo';
        break;
      case 'excel':
        iconClasses += 'ms-Icon--ExcelLogo';
        break;
      case 'xlsx':
        iconClasses += 'ms-Icon--ExcelLogo';
        break;
      case 'powerpoint':
        iconClasses += 'ms-Icon--PowerPointLogo';
        break;
      case 'ppt':
        iconClasses += 'ms-Icon--PowerPointLogo';
        break;
      case 'onenote':
        iconClasses += 'ms-Icon--OneNoteLogo';
        break;
      case 'one':
        iconClasses += 'ms-Icon--OneNoteLogo';
        break;
      case 'visio':
        iconClasses += 'ms-Icon--VisioLogo';
        break;
      case 'vsdx':
        iconClasses += 'ms-Icon--VisioLogo';
        break;
      default:
        iconClasses += 'ms-Icon--TextDocument';
        break;
    }

    return iconClasses;
  }

  public static hexToRGBA(hex: string, alpha: string) {
    let r: number, g: number, b: number;
    if (hex.length === 4) {
      // short format: #f0f
      r = parseInt(hex.substr(1, 1) + hex.substr(1, 1), 16);
      g = parseInt(hex.substr(2, 1) + hex.substr(2, 1), 16);
      b = parseInt(hex.substr(3, 1) + hex.substr(3, 1), 16);
    } else {
      // full format: #ff00ff
      r = parseInt(hex.substr(1, 2), 16);
      g = parseInt(hex.substr(3, 2), 16);
      b = parseInt(hex.substr(5, 2), 16);
    }
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
  }

  public static getRange(startFrom: number, until: number) {
    return Array.from({ length: (until + 1 - startFrom) }, (_, k) => k + startFrom);
  }

  public static getCurrentBootstrapBreakpoint(window: Window): BreakPoints {
    // values from _variables.scss
    const w = window.innerWidth;

    if (w > BreakPoints.xxxl) {
      return BreakPoints.xxxl;
    } else if (w > BreakPoints.xxl) {
      return BreakPoints.xxl;
    } else if (w > BreakPoints.xl) {
      return BreakPoints.xl;
    } else if (w > BreakPoints.lg) {
      return BreakPoints.lg;
    } else if (w > BreakPoints.md) {
      return BreakPoints.md;
    } else if (w > BreakPoints.sm) {
      return BreakPoints.sm;
    }
    return BreakPoints.xs;
  }

  // Detects if Safari is the browser https://stackoverflow.com/questions/7944460/detect-safari-browser/42189492
  public static isSafari(window: Window) {
    return window.navigator.vendor &&
      window.navigator.vendor.indexOf('Apple') > -1 &&
      window.navigator.userAgent &&
      window.navigator.userAgent.indexOf('CriOS') === -1 &&
      window.navigator.userAgent.indexOf('FxiOS') === -1;
  }

  public static isInIframe(window: Window) {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  /**
   * For the MS Teams tab the inTeams query string should be set to true because of authentication reasons.
   * @param window
   */
  public static isInTeamsApp(window: Window) {
    const inTeams =  window.location.search.indexOf(Constants.IN_TEAMS_KEY) > -1 || window.location.hash.indexOf(Constants.IN_TEAMS_KEY) > -1 || window.name === 'extension-tab-frame';
    return inTeams;
  }

  /**
   * Checks if the magic in MS Teams popup querystring/hash is present
   * @param window
   */
  public static inTeamsAuthPopupRedirect(window: Window) {
    const popQueryString = (window.location.search.indexOf(Constants.IN_TEAMS_AUTH_POPUP_REDIRECT_KEY) > -1 || window.location.hash.indexOf(Constants.IN_TEAMS_AUTH_POPUP_REDIRECT_KEY) > -1);
    return popQueryString;
  }

  /**
   * This checks a magic querystring to block the main view from loading.
   * @param window
   */
  public static shouldLoadMain(window: Window) {
    const loadMain = (window.location.search.indexOf(Constants.SHOULD_NOT_LOAD_MAIN_KEY) === -1 && window.location.hash.indexOf(Constants.SHOULD_NOT_LOAD_MAIN_KEY) === -1);
    return loadMain;
  }

  /**
   * This can be used to detect if Workplace is browsed from within an Electron app such as MS Teams
   * @param window
   */
  public static isElectron(window: Window) {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.indexOf(' electron/') > -1;
  }

  public static postMessageToCoreIntranet(message: string, window: Window, origin: string) {
    if (this.isInIframe(window)) {
      window.parent.postMessage(message, `${origin}/*`);
    }
  }

  public static trimValidator: ValidatorFn = (control: FormControl) => {
    if (control.value && control.value.startsWith(' ')) {
      return {
        'trimError': { value: 'control has leading whitespace' }
      };
    }
    return null;
  }

  public static getComponentsOnRoute(route: Route, componentFactoryResolver: ComponentFactoryResolver, vcr: ViewContainerRef) {
    let components: ComponentState[] = [];
    if (route) {
      if (route.component) {
        const routeComponentFactory = componentFactoryResolver.resolveComponentFactory(route.component);
        const routeComponentRef = routeComponentFactory.create(vcr.injector);
        const elementRef = routeComponentRef.location;
        if (elementRef) {
          components = components.concat(Utilities.getComponentsWithinElementRef(elementRef, `/${route.path}`));
        }
        routeComponentRef?.destroy();
      }
      if (route.children?.length > 0) {
        route.children.forEach(childRoute => {
          if (childRoute.component) {
            const routeComponentFactory = componentFactoryResolver.resolveComponentFactory(childRoute.component);
            const routeComponentRef = routeComponentFactory.create(vcr.injector);

            const elementRef = routeComponentRef.location;
            if (elementRef) {
              components = components.concat(Utilities.getComponentsWithinElementRef(elementRef, `/${childRoute.path}`));
            }
            routeComponentRef?.destroy();
          }
        });
      }
    }
    return components;
  }

  public static getComponentsWithinElementRef(elementRef: ElementRef<any>, route?: string): ComponentState[] {
    const viewAsHTMLElement = elementRef.nativeElement as HTMLElement;
    return Utilities.getComponentsWithinElement(viewAsHTMLElement, route);
  }

  public static getComponentsWithinElement(viewAsHTMLElement: HTMLElement, route?: string) {
    const componentList = Array.from(viewAsHTMLElement.querySelectorAll<HTMLElement>('*')).filter(el => el.localName.startsWith('app-'));
    const allComponentEventsOnRoute = componentList.map(element => {
      const componentEvent = new ComponentState({ ComponentId: element.localName, ShowBadge: false, Routes: route ? [route] : [] });
      const headerElement = element.querySelector<HTMLElement>('.widget-header');
      if (headerElement) {
        componentEvent.Component.Title = headerElement.innerText.trim();
      }
      const subHeaderElement = element.querySelector<HTMLElement>('h3.text-muted');
      if (subHeaderElement) {
        componentEvent.Component.Description = subHeaderElement.innerText.trim();
      }
      for (let i = 0; i < element.attributes.length; i++) {
        if (!element.attributes[i].name.startsWith('_')) {
          componentEvent.Component.Configurations[element.attributes[i].name] = element.attributes[i].value;
        }
      }
      return componentEvent;
    });
    return allComponentEventsOnRoute;
  }

  // https://gist.github.com/erikvullings/ada7af09925082cbb89f40ed962d475e#file-deep-copy-ts
  public static deepCopy<T>(target: T) {
    if (target === null) {
      return target;
    }
    if (target instanceof Date) {
      return new Date(target.getTime()) as any;
    }
    if (target instanceof Array) {
      const cp = [] as any[];
      (target as any[]).forEach((v) => { cp.push(v); });
      return cp.map((n: any) => Utilities.deepCopy<any>(n)) as any;
    }
    if (typeof target === 'object' && target !== {}) {
      const cp = { ...(target as { [key: string]: any }) } as { [key: string]: any };
      Object.keys(cp).forEach(k => {
        cp[k] = Utilities.deepCopy<any>(cp[k]);
      });
      return cp as T;
    }
    return target;
  }

    /**
   * Creates a user friendly string by concatenting the news item SenderTag labels
   * @param newsItem Newsitem to get SenderTag from
   * @returns string for displaying the tags for users
   */
  public static getSenderTagString(newsItem: NewsPage) {
      if (newsItem.SenderTags && newsItem.SenderTags.length) {
        let senderTagString = '';
        let maxSenderTags = 2; // Set max to show 2 sendertags
        // If the length of SenderTags array is less than max, set to length of sendertags
        if (newsItem.SenderTags.length < maxSenderTags) {
          maxSenderTags = newsItem.SenderTags.length;
        }
        for (let i = 0; i < maxSenderTags; i++) {
          if (i > 0) {
            senderTagString += ' | ';
          }
          // check if SenderTag is from graph or search
          if (newsItem.SenderTags[i].Label) {
            senderTagString +=  newsItem.SenderTags[i].Label;
          } else {
            senderTagString +=  newsItem.SenderTags[i];
          }
        }
        return senderTagString;
      }
      return null;
  }

  /**
   * User browser language
   * @returns returns the user browser language
   */
  public static getBrowserDefaultLanguage() {
    return navigator.language;
  }

}
